import React, { useEffect } from 'react'
import { generatePath, useHistory, useParams } from 'react-router-dom'

import { AppRoute } from 'routing/AppRoute.enum'
import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { useGetPulseSurveyDetail } from 'hooks/pulseSurveys/useGetPulseSurveyDetail'
import { usePulseSurveys } from 'hooks/usePulseSurveys'
import { PulseSurveyLink } from 'components/PulseSurveyLink'
import { PulseSurveyLinkSkeleton } from 'components/PulseSurveyLink/skeleton'
import { ACTION_ALLOWED_STATUTES } from '../pulseSurvey.helpers'
import { PulseSurveysError } from '../Error'

import { PublishedStatus } from './PublishedStatus'
import { PublishedStatusSkeleton } from './PublishedStatus/skeleton'
import { CloseSurvey } from './CloseSurvey'
import { CloseSurveySkeleton } from './CloseSurvey/skeleton'
import { ParticipationRate } from './ParticipationRate'
import { ParticipationRateSkeleton } from './ParticipationRate/skeleton'
import { EmailTemplates } from './EmailTemplates'
import { EmailTemplatesSkeleton } from './EmailTemplates/skeleton'
import * as S from './status.styles'

export const Status = () => {
    const history = useHistory()

    const params = useParams<{ id: string }>()

    const surveyUUID = params.id

    const { activePulseSurvey, onChangeActivePulseSurvey } = usePulseSurveys()

    const { data, isLoading, isError } = useGetPulseSurveyDetail({ surveyUUID })

    useEffect(() => {
        if (!data) {
            return
        }

        if (data && !activePulseSurvey?.uuid) {
            onChangeActivePulseSurvey(data)
        }

        if (ACTION_ALLOWED_STATUTES.update.includes(data.status)) {
            history.push(generateCompanyURI(generatePath(AppRoute.PulseSurveyCreate, { id: data.uuid })))
        }
    }, [data, activePulseSurvey?.uuid, onChangeActivePulseSurvey, history])

    if (isError) {
        return <PulseSurveysError />
    }
    if (isLoading) {
        return (
            <S.StatusWrapper>
                <S.StatusContainer>
                    <PulseSurveyLinkSkeleton />

                    <PublishedStatusSkeleton />

                    <ParticipationRateSkeleton />

                    <CloseSurveySkeleton />

                    <EmailTemplatesSkeleton />
                </S.StatusContainer>
            </S.StatusWrapper>
        )
    }

    if (!data) {
        return null
    }

    if (!data.survey_dump) {
        return null
    }

    const {
        survey_dump: surveyDump,
        publish_date: publishDate,
        close_date: closeDate,
        respondent_count: completedSurveys,
        target_respondent_count: totalEmployees,
        status: pulseSurveyStatus,
        title: surveyTitle,
        description: surveyDescription,
        apply_anonymity: applyAnonymity,
    } = data

    const { collector_url: surveyUrl } = surveyDump

    return (
        <S.StatusWrapper>
            <S.StatusContainer>
                <PulseSurveyLink surveyUrl={surveyUrl} />

                <PublishedStatus publishDate={publishDate} closeDate={closeDate} />

                <ParticipationRate completedSurveys={completedSurveys} totalEmployees={totalEmployees} />

                <CloseSurvey publishDate={publishDate} pulseSurveyStatus={pulseSurveyStatus} />

                <EmailTemplates
                    surveyTitle={surveyTitle}
                    surveyUrl={surveyUrl}
                    surveyDescription={surveyDescription}
                    applyAnonymity={applyAnonymity}
                />
            </S.StatusContainer>
        </S.StatusWrapper>
    )
}
